// ═══════════════════════════════════════════════════════════════════════════════
// Helper — Money Received period select (styled overlay select)
// ═══════════════════════════════════════════════════════════════════════════════

import { useMemo, type ChangeEvent } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

export type HelperMoneyReceivedPeriodOption = {
  id: string;
  startDate: string;
  endDate: string;
};

type HelperMoneyReceivedPeriodSelectProps = {
  id: string;
  ariaLabel: string;
  budgets: HelperMoneyReceivedPeriodOption[];
  value: string;
  onChange: (budgetId: string) => void;
  disabled?: boolean;
};

/** Soft green halo + ring — always visible; stronger on focus-within */
const PERIOD_FIELD_CLASS =
  'flex min-h-11 items-center gap-2 rounded-xl border-2 border-primary-600 bg-white px-3 shadow-[0_0_22px_rgba(11,110,79,0.16),0_0_0_3px_rgba(16,185,129,0.18)] transition-shadow focus-within:border-primary-600 focus-within:shadow-[0_0_28px_rgba(11,110,79,0.26),0_0_0_5px_rgba(16,185,129,0.28)] dark:border-primary-500 dark:bg-gray-900 dark:shadow-[0_0_22px_rgba(52,211,153,0.12),0_0_0_3px_rgba(52,211,153,0.16)] dark:focus-within:shadow-[0_0_28px_rgba(52,211,153,0.2),0_0_0_5px_rgba(52,211,153,0.24)]';

export const HelperMoneyReceivedPeriodSelect = ({
  id,
  ariaLabel,
  budgets,
  value,
  onChange,
  disabled = false,
}: HelperMoneyReceivedPeriodSelectProps) => {
  const { t } = useLanguage();

  const selectedBudget = useMemo(
    () => budgets.find((row) => row.id === value) ?? null,
    [budgets, value]
  );

  const selectedPeriodLabel = useMemo(() => {
    if (selectedBudget) {
      return t('settlement.period', {
        start: selectedBudget.startDate,
        end: selectedBudget.endDate,
      });
    }
    if (budgets.length === 0) {
      return t('budget.empty');
    }
    return '';
  }, [budgets.length, selectedBudget, t]);

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextBudgetId = event.target.value;
    if (nextBudgetId === '') {
      return;
    }
    onChange(nextBudgetId);
  };

  return (
    <div className="overflow-hidden rounded-[14px]">
      <div className={`${PERIOD_FIELD_CLASS} relative`}>
        <svg
          className="pointer-events-none h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="pointer-events-none min-w-0 flex-1 truncate pl-0.5 pr-2 text-sm font-medium leading-none text-gray-900 dark:text-gray-100">
          {selectedPeriodLabel}
        </span>
        <svg
          className="pointer-events-none h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        <select
          id={id}
          value={value}
          onChange={handleChange}
          disabled={disabled || budgets.length === 0}
          className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          aria-label={ariaLabel}
        >
          {budgets.length === 0 ? <option value="">{t('budget.empty')}</option> : null}
          {budgets.map((row) => (
            <option key={row.id} value={row.id}>
              {t('settlement.period', { start: row.startDate, end: row.endDate })}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
